import moment from "moment";

/**
 * Selects iqamah times for a given date from the API result.
 * Rules:
 * - Use entries from `iqamaTimes` whose `iqama_date` is <= target date (no future picks).
 * - If multiple entries qualify, pick the latest by `iqama_date`.
 * - If multiple entries exist for the same day, prefer the one with a later `debug_information.manual_entry_date` if present.
 * - Returns an object with prayer keys and time strings, or null if missing.
 *
 * @param {Object} apiResult - Full JSON result from the masjidi API endpoint.
 * @param {number} year - Gregorian year, e.g. 2026
 * @param {number} month - Gregorian month 1-12
 * @param {number} day - Gregorian day of month 1-31
 * @returns {{fajr: string|null, zuhr: string|null, asr: string|null, maghrib: string|null, isha: string|null}} iqamah times
 */
export function pickIqamahTimesForDate(apiResult, year, month, day) {
  if (!apiResult || !Array.isArray(apiResult.iqamaTimes)) {
    return { fajr: null, zuhr: null, asr: null, maghrib: null, isha: null };
  }

  const targetDate = moment({ year, month: month - 1, day }).startOf("day");

  // Normalize items with parsed dates and filter out future iqama dates
  const normalized = apiResult.iqamaTimes
    .map((item) => {
      const iqamaDateStr =
        (item && item.iqama_date && item.iqama_date.date) ||
        (item && item.date && item.date.date) ||
        null; // fallback if iqama_date missing
      const iqamaDate = iqamaDateStr ? moment(iqamaDateStr).startOf("day") : null;
      const manualEntryStr =
        (item && item.debug_information && item.debug_information.manual_entry_date) ||
        null;
      const manualEntryDate = manualEntryStr ? moment(manualEntryStr) : null;

      // convenience: detect any non-empty iqamah
      const hasAnyIqamah = [
        "fajr_iqama",
        "zuhr_iqama",
        "asr_iqama",
        "maghrib_iqama",
        "isha_iqama",
        "fajr",
        "dhuhr",
        "asr",
        "maghrib",
        "isha",
      ].some((k) => typeof item[k] === "string" && item[k].trim().length > 0);

      return { item, iqamaDate, manualEntryDate, hasAnyIqamah };
    })
    .filter((x) => x.iqamaDate && !x.iqamaDate.isAfter(targetDate))
    .filter((x) => x.hasAnyIqamah); // ignore entries with all empty iqamah times

  if (normalized.length === 0) {
    return { fajr: null, zuhr: null, asr: null, maghrib: null, isha: null };
  }

  // Sort by iqamaDate ascending, then by manualEntryDate ascending if on same day
  normalized.sort((a, b) => {
    if (a.iqamaDate.isBefore(b.iqamaDate)) return -1;
    if (a.iqamaDate.isAfter(b.iqamaDate)) return 1;
    // same day: prefer later manual entry
    if (a.manualEntryDate && b.manualEntryDate) {
      if (a.manualEntryDate.isBefore(b.manualEntryDate)) return -1;
      if (a.manualEntryDate.isAfter(b.manualEntryDate)) return 1;
    }
    return 0;
  });

  const chosen = normalized[normalized.length - 1].item;

  const normalizeIqamahTime = (value) => {
    if (typeof value !== "string" || value.trim().toLowerCase() === "sunset") {
      return null;
    }
    return value.trim() || null;
  };

  // Support both *_iqama and consolidated fields if present
  const fajr = normalizeIqamahTime(chosen.fajr_iqama || chosen.fajr);
  const zuhr = normalizeIqamahTime(chosen.zuhr_iqama || chosen.dhuhr);
  const asr = normalizeIqamahTime(chosen.asr_iqama || chosen.asr);
  const maghrib = normalizeIqamahTime(chosen.maghrib_iqama || chosen.maghrib);
  const isha = normalizeIqamahTime(chosen.isha_iqama || chosen.isha);

  return { fajr, zuhr, asr, maghrib, isha };
}

/**
 * Optional helper: convert returned iqamah strings into moment instances aligned
 * to a provided calendar date, suitable for existing logic.
 */
export function convertIqamahToMoment(iqamahObj, year, month, day) {
  const toMoment = (timeStr) =>
    timeStr
      ? moment(moment(`${timeStr} ${year}:${month}:${day}`, "hh:mm a YYYY:MM:DD").seconds(0).toString())
      : null;

  return {
    fajr: toMoment(iqamahObj.fajr),
    zuhr: toMoment(iqamahObj.zuhr),
    asr: toMoment(iqamahObj.asr),
    maghrib: toMoment(iqamahObj.maghrib),
    isha: toMoment(iqamahObj.isha),
  };
}
